import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, message, Table, DatePicker, Statistic, Progress, Space, Button, Tag, Divider, Tooltip, Badge } from 'antd';
import {
    WalletOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    DashboardOutlined,
    TagsOutlined,
    PlusOutlined,
    FileTextOutlined,
    LineChartOutlined,
    PieChartOutlined,
    ThunderboltOutlined,
    BankOutlined,
    HistoryOutlined,
    ExportOutlined,
    InboxOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { formatMoneyJOD } from '../../utils/formatters';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const AccountingDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dates, setDates] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
    const [data, setData] = useState({
        summary: {
            totalRevenue: 0,
            totalExpenses: 0,
            netProfit: 0
        },
        expenseBreakdown: [],
        timeline: []
    });

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const from = dates[0].format('YYYY-MM-DD');
            const to = dates[1].format('YYYY-MM-DD');
            const headers = { Authorization: `Bearer ${token}` };

            const res = await axios.get(`/api/accounting/reports/summary?startDate=${from}&endDate=${to}`, { headers });
            // Merge with defaults so partial API responses never crash the UI
            setData(prev => ({
                summary: {
                    totalRevenue: 0,
                    totalExpenses: 0,
                    netProfit: 0,
                    ...(res.data?.summary || {})
                },
                expenseBreakdown: Array.isArray(res.data?.expenseBreakdown) ? res.data.expenseBreakdown : [],
                timeline: Array.isArray(res.data?.timeline) ? res.data.timeline : []
            }));
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.error || 'Failed to load accounting data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [dates]);

    const breakdownColumns = [
        {
            title: 'Category',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    <Badge color={record.color} />
                    <Text strong>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Total Spent',
            dataIndex: 'value',
            key: 'value',
            render: (val) => <Text strong>{formatMoneyJOD(val)}</Text>,
            align: 'right',
            sorter: (a, b) => a.value - b.value
        },
        {
            title: 'Share',
            key: 'percent',
            render: (_, record) => {
                const total = data.summary?.totalExpenses || 0;
                const percent = total > 0 ? ((record.value || 0) / total) * 100 : 0;
                return (
                    <div style={{ width: 100 }}>
                        <Progress
                            percent={parseFloat(percent.toFixed(1))}
                            size="small"
                            strokeColor={record.color}
                            format={(p) => `${p}%`}
                        />
                    </div>
                );
            },
            align: 'right'
        }
    ];

    const profitMargin = (data.summary?.totalRevenue || 0) > 0
        ? (((data.summary?.netProfit || 0) / (data.summary?.totalRevenue || 1)) * 100).toFixed(1)
        : 0;

    const safeTimeline = Array.isArray(data.timeline) ? data.timeline : [];
    const safeBreakdown = Array.isArray(data.expenseBreakdown) ? data.expenseBreakdown : [];
    const safeSummary = data.summary || { totalRevenue: 0, totalExpenses: 0, netProfit: 0 };
    const maxTimelineValue = safeTimeline.length > 0
        ? Math.max(...safeTimeline.map(t => parseFloat(t.value) || 0), 1)
        : 1;

    return (
        <div style={{ width: '100%' }}>
            {/* Page Header */}
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0 }} data-v2-refactor="true">Accounting Dashboard</Title>
                    <Text type="secondary">Financial overview and expense analytics (REFACTORED V2)</Text>
                </div>
                <div style={{ background: '#fff', padding: '8px 16px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}>
                    <Text strong style={{ marginRight: 12, color: '#8c8c8c' }}>PERIOD:</Text>
                    <RangePicker
                        value={dates}
                        onChange={setDates}
                        allowClear={false}
                        bordered={false}
                        style={{ width: 280 }}
                    />
                </div>
            </div>

            {/* KPI Cards Row */}
            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} className="premium-card shadow-sm" bodyStyle={{ padding: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Statistic
                                title={<Text type="secondary" strong style={{ fontSize: '12px' }}>TOTAL REVENUE</Text>}
                                value={data.summary.totalRevenue}
                                valueStyle={{ color: '#3f8600', fontWeight: 800, fontSize: '28px' }}
                                formatter={(val) => formatMoneyJOD(val)}
                            />
                            <div style={{ background: '#f6ffed', padding: '12px', borderRadius: '12px' }}>
                                <LineChartOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                            </div>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <Tag color="success" icon={<ArrowUpOutlined />}>Verified Income</Tag>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} className="premium-card shadow-sm" bodyStyle={{ padding: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Statistic
                                title={<Text type="secondary" strong style={{ fontSize: '12px' }}>TOTAL EXPENSES</Text>}
                                value={data.summary.totalExpenses}
                                valueStyle={{ color: '#cf1322', fontWeight: 800, fontSize: '28px' }}
                                formatter={(val) => formatMoneyJOD(val)}
                            />
                            <div style={{ background: '#fff1f0', padding: '12px', borderRadius: '12px' }}>
                                <WalletOutlined style={{ fontSize: '24px', color: '#f5222d' }} />
                            </div>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <Tag color="error" icon={<ArrowDownOutlined />}>Operational Costs</Tag>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} className="premium-card shadow-sm" bodyStyle={{ padding: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Statistic
                                title={<Text type="secondary" strong style={{ fontSize: '12px' }}>NET PROFIT</Text>}
                                value={data.summary.netProfit}
                                valueStyle={{ color: data.summary.netProfit >= 0 ? '#1890ff' : '#cf1322', fontWeight: 800, fontSize: '28px' }}
                                formatter={(val) => formatMoneyJOD(val)}
                            />
                            <div style={{ background: '#e6f7ff', padding: '12px', borderRadius: '12px' }}>
                                <BankOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                            </div>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <Tag color="processing">Cash Flow</Tag>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} className="premium-card shadow-sm" bodyStyle={{ padding: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Statistic
                                title={<Text type="secondary" strong style={{ fontSize: '12px' }}>PROFIT MARGIN</Text>}
                                value={profitMargin}
                                valueStyle={{ color: '#722ed1', fontWeight: 800, fontSize: '28px' }}
                                suffix="%"
                            />
                            <div style={{ background: '#f9f0ff', padding: '12px', borderRadius: '12px' }}>
                                <ThunderboltOutlined style={{ fontSize: '24px', color: '#722ed1' }} />
                            </div>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <Progress percent={parseFloat(profitMargin)} size="small" showInfo={false} strokeColor="#722ed1" />
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]}>
                {/* Spending Trends */}
                <Col xs={24} lg={16}>
                    <Card
                        title={<Space><LineChartOutlined /> Spending Trends</Space>}
                        extra={<Button type="text" icon={<HistoryOutlined />}>View History</Button>}
                        className="premium-card shadow-sm"
                        style={{ height: '100%' }}
                    >
                        {safeTimeline.length > 0 ? (
                            <div style={{ padding: '20px 0' }}>
                                <div style={{ height: '300px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: '8px' }}>
                                    {safeTimeline.map((t, idx) => {
                                        const heightPercent = ((parseFloat(t.value) || 0) / maxTimelineValue) * 100;
                                        return (
                                            <Tooltip key={idx} title={
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: '11px', opacity: 0.8 }}>{dayjs(t.label).format('MMM DD, YYYY')}</div>
                                                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{formatMoneyJOD(t.value)}</div>
                                                </div>
                                            }>
                                                <div style={{
                                                    flex: 1,
                                                    height: `${Math.max(4, heightPercent)}%`,
                                                    backgroundColor: '#1890ff',
                                                    background: 'linear-gradient(180deg, #40a9ff 0%, #1890ff 100%)',
                                                    borderRadius: '6px 6px 0 0',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    position: 'relative'
                                                }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.filter = 'brightness(1.1)';
                                                        e.target.style.transform = 'scaleX(1.05)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.filter = 'none';
                                                        e.target.style.transform = 'none';
                                                    }}
                                                />
                                            </Tooltip>
                                        );
                                    })}
                                </div>
                                <div style={{ borderTop: '1px solid #f0f0f0', marginTop: '16px', paddingTop: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600 }}>{dates[0].format('MMM DD, YYYY')}</Text>
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600 }}>DAILY EXPENDITURE TIMELINE</Text>
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600 }}>{dates[1].format('MMM DD, YYYY')}</Text>
                                </div>
                            </div>
                        ) : (
                            <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', background: '#fafafa', borderRadius: '8px' }}>
                                <div style={{ fontSize: '40px', color: '#d9d9d9', marginBottom: '16px' }}><HistoryOutlined /></div>
                                <Text type="secondary">No spending data available for the selected period</Text>
                            </div>
                        )}
                    </Card>
                </Col>

                {/* Right Sidebar: Quick Actions & Categories */}
                <Col xs={24} lg={8}>
                    <Space direction="vertical" size={24} style={{ width: '100%' }}>
                        <Card title="Quick Actions" className="premium-card shadow-sm">
                            <Row gutter={[12, 12]}>
                                <Col span={12}>
                                    <Button 
                                        block 
                                        type="primary" 
                                        icon={<PlusOutlined />} 
                                        style={{ height: 'auto', padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: '8px' }}
                                        onClick={() => navigate('/accounting/expenses')}
                                    >
                                        Add Expense
                                    </Button>
                                </Col>
                                <Col span={12}>
                                    <Button 
                                        block 
                                        icon={<FileTextOutlined />} 
                                        style={{ height: 'auto', padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: '8px' }}
                                        onClick={() => navigate('/accounting/reports')}
                                    >
                                        Reports
                                    </Button>
                                </Col>
                                <Col span={12}>
                                    <Button 
                                        block 
                                        icon={<TagsOutlined />} 
                                        style={{ height: 'auto', padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: '8px' }}
                                        onClick={() => navigate('/accounting/categories')}
                                    >
                                        Categories
                                    </Button>
                                </Col>
                                <Col span={12}>
                                    <Button 
                                        block 
                                        icon={<ExportOutlined />} 
                                        style={{ height: 'auto', padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: '8px' }}
                                    >
                                        Export
                                    </Button>
                                </Col>
                            </Row>
                        </Card>

                        <Card
                            title={<Space><PieChartOutlined /> Top Categories</Space>}
                            className="premium-card shadow-sm"
                            bodyStyle={{ padding: 0 }}
                        >
                            <Table
                                dataSource={safeBreakdown.slice().sort((a, b) => (b.value || 0) - (a.value || 0)).slice(0, 5)}
                                columns={breakdownColumns}
                                pagination={false}
                                rowKey="name"
                                size="middle"
                                loading={loading}
                            />
                        </Card>
                    </Space>
                </Col>
            </Row>
        </div>
    );
};

export default AccountingDashboard;
